import { isAxiosError } from 'axios';
import { Result, err, ok } from 'neverthrow';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import axios from '@utils/axios';

import { TMSLocation } from 'types/Load';
import { ApiError } from 'types/api/ApiError';
import captureException from 'utils/captureException';

export type GetLocationDetailsResponse = {
  location: TMSLocation;
  message: string;
};

/**
 * Fetches full location details by external TMS id (e.g. for locations that came
 * from search and may be missing phone/dock hours). Only supported by some TMS
 * integrations (e.g. GlobalTranz). customerID is required for those that need it.
 */
export async function getLocationDetails(
  tmsID: number,
  externalTMSID: string,
  customerID?: number,
  customerExternalTmsId?: string
): Promise<Result<GetLocationDetailsResponse, ApiError>> {
  try {
    const params = new URLSearchParams({
      tmsID: tmsID.toString(),
      externalTMSID,
    });
    if (customerID != null) {
      params.append('customerID', customerID.toString());
    }
    if (customerExternalTmsId != null && customerExternalTmsId !== '') {
      params.append('customerExternalTmsId', customerExternalTmsId);
    }
    const response = await axios.get<GetLocationDetailsResponse>(
      `/locations/details?${params.toString()}`
    );
    return ok(response.data);
  } catch (error) {
    captureException(error, { functionName: 'getLocationDetails' });

    if (!isAxiosError(error)) {
      return err({ message: 'Failed to get location details from TMS' });
    }

    // 501 = not implemented for this TMS
    if (error.response?.status === 501) {
      return err({
        message:
          (error.response?.data as GetLocationDetailsResponse)?.message ||
          'Location details are not supported for this TMS',
      });
    }

    return err({
      message:
        (error.response?.data as GetLocationDetailsResponse)?.message ||
        'Failed to get location details from TMS',
    });
  }
}
